import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import { PlatformProvider, usePlatform } from '@/contexts/PlatformContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import { PublicOnly, AuthGuard, TermsGuard, ApplicantActionsGuard } from '@/components/RouteGuards';
import DashboardLayout from '@/components/DashboardLayout';
import { Skeleton } from './components/ui/skeleton';

// Pages
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const OAuthCompletePage = lazy(() => import('@/pages/OAuthCompletePage'));
const EligibilityErrorPage = lazy(() => import('@/pages/EligibilityErrorPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const AvailableJobsPage = lazy(() => import('@/pages/AvailableJobsPage'));
const JobDetailPage = lazy(() => import('@/pages/JobDetailPage'));
const MyApplicationsPage = lazy(() => import('@/pages/MyApplicationsPage'));
const ApplicationFormPage = lazy(() => import('@/pages/ApplicationFormPage'));
const ApplicationSummaryPage = lazy(
  () => import('@/pages/ApplicationSummaryPage'),
);
const MyProfilePage = lazy(() => import('@/pages/MyProfilePage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));
const PortalClosedPage = lazy(() => import('@/pages/PortalClosedPage'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage'));
const SetupPasswordPage = lazy(() => import('@/pages/SetupPasswordPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// import OAuthCompletePage from '@/pages/OAuthCompletePage';
// import EligibilityErrorPage from '@/pages/EligibilityErrorPage';
// import TermsPage from '@/pages/TermsPage';
// import DashboardPage from '@/pages/DashboardPage';
// import AvailableJobsPage from '@/pages/AvailableJobsPage';
// import JobDetailPage from '@/pages/JobDetailPage';
// import MyApplicationsPage from '@/pages/MyApplicationsPage';
// import ApplicationFormPage from '@/pages/ApplicationFormPage';
// import ApplicationSummaryPage from '@/pages/ApplicationSummaryPage';
// import MyProfilePage from '@/pages/MyProfilePage';
// import ComingSoonPage from '@/pages/ComingSoonPage';
// import PortalClosedPage from '@/pages/PortalClosedPage';
// import MaintenancePage from '@/pages/MaintenancePage';
// import SetupPasswordPage from '@/pages/SetupPasswordPage';
// import NotFound from '@/pages/NotFound';

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="space-y-4 p-2">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-48" />
      <div className="mt-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isComingSoon, isMaintenance, loading } = usePlatform();
  const navigate = useNavigate();

  // When isComingSoon flips from true → false (after a refetch), navigate to login.
  // This runs after the render where isComingSoon is already false, so the normal
  // route tree is active and /login resolves correctly.
  const prevIsComingSoon = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading) return;
    if (prevIsComingSoon.current === true && !isComingSoon) {
      navigate('/login', { replace: true });
    }
    prevIsComingSoon.current = isComingSoon;
  }, [isComingSoon, loading, navigate]);

  if (loading) return null;

  if (isMaintenance) {
    return (
      <Routes>
        <Route path="/auth/complete" element={<OAuthCompletePage />} />
        <Route
          path="/auth/eligibility-error"
          element={<EligibilityErrorPage />}
        />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="*" element={<Navigate to="/maintenance" replace />} />
      </Routes>
    );
  }

  if (isComingSoon) {
    return (
      <Routes>
        <Route path="/auth/complete" element={<OAuthCompletePage />} />
        <Route
          path="/auth/eligibility-error"
          element={<EligibilityErrorPage />}
        />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        <Route path="/coming-soon" element={<ComingSoonPage />} />
        <Route path="*" element={<Navigate to="/coming-soon" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route path="/auth/complete" element={<OAuthCompletePage />} />
      <Route
        path="/auth/eligibility-error"
        element={<EligibilityErrorPage />}
      />
      <Route path="/setup-password" element={<SetupPasswordPage />} />

      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route path="/closed" element={<PortalClosedPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route
        path="/agree"
        element={
          <AuthGuard>
            <TermsPage />
          </AuthGuard>
        }
      />

      <Route
        element={
          <TermsGuard>
            <DashboardLayout />
          </TermsGuard>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route
          path="/jobs"
          element={
            <ApplicantActionsGuard>
              <AvailableJobsPage />
            </ApplicantActionsGuard>
          }
        />
        <Route
          path="/jobs/:jobId"
          element={
            <ApplicantActionsGuard>
              <JobDetailPage />
            </ApplicantActionsGuard>
          }
        />
        <Route path="/applications" element={<MyApplicationsPage />} />
        <Route
          path="/applications/:id/edit"
          element={
            <ApplicantActionsGuard>
              <ApplicationFormPage />
            </ApplicantActionsGuard>
          }
        />
        <Route
          path="/applications/:id/summary"
          element={<ApplicationSummaryPage />}
        />
        <Route path="/profile" element={<MyProfilePage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <PlatformProvider>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </PlatformProvider>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
